import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DsyInputDropdown } from '@dsy/dsyui';
import { FilterEntry } from '../../../models/filters.model';

type DsyDropdownHost = HTMLElement & { isOpen?: boolean; updateComplete?: Promise<unknown> };
type DsyListCustomConf = NonNullable<InstanceType<typeof DsyInputDropdown>['customDsyListConfObject']>;

@Component({
  selector: 'dr-ops-stream-ui-searchable-dropdown',
  templateUrl: './searchable-dropdown.component.html',
  styleUrls: ['./searchable-dropdown.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: { style: 'display: contents' },
})
export class SearchableDropdownComponent implements AfterViewInit, OnDestroy {
  readonly entries = input.required<FilterEntry[]>();
  readonly label = input<string>('');
  readonly placeholder = input<string>('Select an option...');
  readonly searchPlaceholder = input<string>('Search...');
  readonly exclusiveSelection = input<boolean>(true);
  readonly closesOnSelection = input<boolean>(true);
  readonly customDsyListConfObject = input<DsyListCustomConf>({});

  /** Emits the labelContent of the selected entry (compatible with the existing onXChange handlers) */
  readonly handleChange = input<((value: string) => void) | undefined>(undefined);

  protected readonly containerStyle = 'padding-top: 48px;';
  protected readonly search = signal('');
  protected readonly isOpen = signal(false);

  protected readonly ddRef = viewChild<ElementRef<DsyDropdownHost>>('dd');
  protected readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly filteredEntries = computed<FilterEntry[]>(() => {
    const term = this.search().toLowerCase().trim();
    const entries = this.entries();
    if (!term) return entries;
    return entries.filter((e) => e.labelContent?.toLowerCase().includes(term));
  });

  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private resizeObs?: ResizeObserver;

  private readonly syncOpen = () => {
    const el = this.ddRef()?.nativeElement;
    const wasOpen = this.isOpen();
    const open = !!el?.isOpen;
    this.isOpen.set(open);
    if (open && !wasOpen) {
      setTimeout(() => this.searchInputRef()?.nativeElement.focus(), 0);
    }
    if (!open && wasOpen) {
      this.search.set('');
    }
  };

  private readonly handleOutsideMousedown = (event: MouseEvent) => {
    if (!this.isOpen()) return;
    const host = this.ddRef()?.nativeElement;
    if (!host) return;
    const path = event.composedPath();
    if (path.includes(this.elRef.nativeElement) || path.includes(host)) return;
    host.isOpen = false;
    this.isOpen.set(false);
    this.search.set('');
  };

  private async observePanelWidth() {
    const host = this.ddRef()?.nativeElement;
    if (!host) return;
    if (host.updateComplete) await host.updateComplete;
    const panel = host.shadowRoot?.querySelector('#dropdown-wrapper') as HTMLElement | null;
    if (!panel) return;
    const update = () => {
      const w = panel.getBoundingClientRect().width;
      this.elRef.nativeElement.style.setProperty('--dd-panel-width', `${w}px`);
    };
    this.resizeObs = new ResizeObserver(update);
    this.resizeObs.observe(panel);
    update();
  }

  ngAfterViewInit() {
    document.addEventListener('click', this.syncOpen);
    document.addEventListener('mousedown', this.handleOutsideMousedown, true);
    this.observePanelWidth();
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.syncOpen);
    document.removeEventListener('mousedown', this.handleOutsideMousedown, true);
    this.resizeObs?.disconnect();
  }

  protected onSearchInput(event: Event) {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected stopEvent(event: Event) {
    event.stopPropagation();
  }
}
